import React, { useRef, useState, useEffect } from "react";
import { Modal, Box, Typography, Button } from "@mui/material";
import { css } from "aphrodite";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { RequestLayout } from "../RequestLayout";
import { secondsToString } from "../../../../Utils";

const OtpModal = ({
    visible,
    validateButtonText = "Validate OTP",
    validateButtonColor = "primary",
    phoneOrEmail = "+30 69XXXXXXXX",
    isLoading = false,
    error = "",
    timer = 30,
    onClose = () => {},
    onValidateOtp = (_otpFinalValue) => {},
    handleResendOtp = () => {},
    handleReturnToHome = () => {},
}) => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputsRef = useRef([]);
    const previouslyFocusedRef = useRef(null);
    const [t] = useTranslation(["translation"]);
    const [timeLeft, setTimeLeft] = useState(timer);
    const [canResend, setCanResend] = useState(false);
    const [isLockedOut, setIsLockedOut] = useState(false);

    useEffect(() => {
        if (error?.code === 13) {
            setTimeLeft(120);
            setCanResend(false);
            setIsLockedOut(true);
        }
    }, [error?.code]);

    useEffect(() => {
        let interval;
        if (!canResend && visible && timeLeft > 0) {
            interval = setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [canResend, visible, timeLeft]);

    useEffect(() => {
        if (visible) {
            previouslyFocusedRef.current = document.activeElement;
            setCanResend(false);
            setTimeLeft(timer);
            setIsLockedOut(false);
            setOtp(["", "", "", "", "", ""]);
            setTimeout(() => {
                inputsRef.current[0]?.focus();
            }, 100);
        } else if (previouslyFocusedRef.current) {
            previouslyFocusedRef.current.focus?.();
            previouslyFocusedRef.current = null;
        }
    }, [visible, timer]);

    const handleChange = (index, value) => {
        if (!/^\d?$/.test(value) || isLockedOut) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (isLockedOut) return;
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
        if (e.key === "Enter") {
            handleValidate();
        }
    };

    const handleValidate = () => {
        const finalCode = otp.join("");
        if (finalCode.length === 6 && !isLockedOut) {
            onValidateOtp(finalCode);
        }
    };

    const handleResend = () => {
        if (canResend) {
            setOtp(["", "", "", "", "", ""]);
            setIsLockedOut(false);
            handleResendOtp();
            setCanResend(false);
            setTimeLeft(timer);
        }
    };

    return (
        <Modal
            open={visible}
            onClose={onClose}
            aria-labelledby="otp-modal-title"
            aria-describedby="otp-modal-description"
        >
            <Box>
                <RequestLayout loading={isLoading} overlayLoading>
                    <Box className={css(styles.modal)} role="dialog" aria-modal="true" aria-labelledby="otp-modal-title">
                        <Typography
                            id="otp-modal-title"
                            variant="h6"
                            className={css(styles.heading)}
                        >
                            {t("Please Enter the OTP for Verification.")}
                        </Typography>
                        <Typography
                            id="otp-modal-description"
                            variant="body2"
                            className={css(styles.subtext)}
                        >
                            {t("An OTP (One Time Password) has been sent to ")} {phoneOrEmail}
                        </Typography>

                        <Box className={css(styles.otpBox)}>
                            {otp.map((digit, idx) => (
                                <input
                                    key={idx}
                                    maxLength={1}
                                    value={digit}
                                    ref={(el) => (inputsRef.current[idx] = el)}
                                    onChange={(e) => handleChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                    className={css(styles.otpInput)}
                                    type="text"
                                    inputMode="numeric"
                                    disabled={isLockedOut}
                                    aria-label={t("otpDigit", { number: idx + 1 })}
                                />
                            ))}
                        </Box>

                        {isLockedOut && (
                            <Typography 
                                variant="body2" 
                                sx={{ color: "red", my: 2, textAlign: "center", fontWeight: 'bold' }}
                                role="alert"
                            >
                                {t("The OTP has been invalidated due to multiple failed attempts. Please wait for the resend option to become available.")}
                            </Typography>
                        )}

                        <Button
                            variant="text"
                            onClick={handleResend}
                            disabled={!canResend}
                            className={css(styles.resend)}
                            aria-label={t("Resend OTP")}
                            sx={{
                                textTransform: "none",
                                opacity: canResend ? 1 : 0.4,
                                marginBottom: "20px",
                            }}
                        >
                            {canResend
                                ? t("Resend OTP")
                                : `${t("Resend again in")} ${secondsToString(timeLeft)}`}
                        </Button>

                        <Button
                            disableElevation
                            variant="contained"
                            color={validateButtonColor}
                            onClick={handleValidate}
                            className={css(styles.validateButton)}
                            disabled={otp.join("").length !== 6 || isLockedOut || canResend}
                        >
                            {validateButtonText}
                        </Button>

                        <Button
                            variant="text"
                            onClick={onClose}
                            className={css(styles.backHome)}
                            aria-label={t("back")}
                            sx={{
                                textTransform: "none",
                                marginTop: "15px",
                            }}
                        >
                            {t("Back")}
                        </Button>
                    </Box>
                </RequestLayout>
            </Box>
        </Modal>
    );
};

export default OtpModal;
